import { graphql } from "@acto/gql";
import { PaginatedDataAccessResult, PagingOptions } from "@acto/taskpane/core/data-access/types";
import { useMemo } from "react";
import { GetMeetingsQuery, MeetingOrderBy, MeetingScope } from "@acto/gql/graphql";
import { usePagination } from "@acto/taskpane/core/data-access/pagination/usePagination";
import { hasClient, Meeting } from "@acto/taskpane/core/domain";

/**
 * Practical upper bound for fetching all meetings in a single query.
 * Use this when you need to fetch all meetings without pagination (e.g., for a single day).
 * 1000 meetings per day is an unrealistic scenario, making this a safe upper limit.
 */
export const UNLIMITED_MEETINGS = 1000;

const MeetingsQuery = graphql(`
  query GetMeetings(
    $first: Int!
    $after: String
    $filter: MeetingFilter
    $orderBy: MeetingOrderBy
    $withAddress: Boolean! = false
  ) {
    meetings(first: $first, after: $after, filter: $filter, orderBy: $orderBy) {
      __typename
      edges {
        __typename
        cursor
        node {
          id
          __typename
          scheduledDate
          scheduledStartTime
          scheduledEndTime
          occurrenceDate
          series {
            id
            __typename
            cadence
            startDate
            until
            scheduledStartTime
            scheduledEndTime
          }
          client {
            id
            legacyId
            __typename
            name
            externalReference
            street @include(if: $withAddress)
            city @include(if: $withAddress)
          }
          owner {
            id
            __typename
            name
            employee {
              id
              __typename
              name
            }
          }
          note {
            id
            __typename
            clientId
            meetingId
            content
            title
            createdAt
            updatedAt
            openTodosCount
          }
        }
      }
      pageInfo {
        __typename
        hasNextPage
        endCursor
      }
    }
  }
`);

interface MeetingFilterOptions {
  clientId?: string;
  scheduledDate?: string;
  /**
   * Window of `scheduledDate`s to return (materialized meetings plus expanded recurring-series
   * occurrences). Prefer this over the default row list when the caller renders a bounded span
   * (e.g. a calendar week) — the backend row list is ordered by `created_at`, so an unbounded
   * `limit` returns the most-recently-created meetings, not those nearest the visible dates.
   */
  dateRange?: { startDate: string; endDate: string };
  standaloneOnly?: boolean;
  fromDate?: string;
  hasNote?: boolean;
  orderBy?: MeetingOrderBy;
  /** Fetch client street/city (off by default — only the home calendar's meeting list needs them). */
  includeAddress?: boolean;
  /** Only `CLIENT_ACCESS` can return an `owner` other than the caller. */
  scope?: MeetingScope;
  /** Set false to hold the query until the filter it depends on is known. */
  enabled?: boolean;
}

export function useMeetings({
  clientId,
  scheduledDate,
  dateRange,
  standaloneOnly,
  fromDate,
  hasNote,
  orderBy,
  limit = 10,
  includeAddress = false,
  scope = MeetingScope.Owned,
  enabled = true,
}: PagingOptions & MeetingFilterOptions): PaginatedDataAccessResult<Meeting> {
  // Keyed on the range's primitive bounds so an inline `dateRange` object literal keeps the filter stable.
  const rangeStart = dateRange?.startDate;
  const rangeEnd = dateRange?.endDate;
  const clientScoped = scope === MeetingScope.ClientAccess;
  const variables = useMemo(
    () => ({
      filter: {
        clientId,
        scheduledDate,
        ...(rangeStart && rangeEnd && { dateRange: { startDate: rangeStart, endDate: rangeEnd } }),
        // Unlike hasNote, false means "no filter" server-side; strip it so both spellings share one cache slot.
        ...(standaloneOnly && { standaloneOnly }),
        ...(fromDate && { fromDate }),
        ...(hasNote !== undefined && { hasNote }),
        // OWNED is the server default; omit it so the narrow read keeps one cache slot either way.
        ...(clientScoped && { scope }),
      },
      ...(orderBy && { orderBy }),
      withAddress: includeAddress,
    }),
    [
      clientId,
      scheduledDate,
      rangeStart,
      rangeEnd,
      standaloneOnly,
      fromDate,
      hasNote,
      orderBy,
      includeAddress,
      scope,
      clientScoped,
    ],
  );

  const { data, isFetching, isPending, error, hasNextPage, fetchNextPage, refreshAfter, refetch } = usePagination({
    query: MeetingsQuery,
    limit,
    connection: (data: GetMeetingsQuery) => data.meetings,
    variables,
    enabled,
  });

  const meetings = useMemo(() => data?.filter(hasClient).map(Meeting.fromGraphql), [data]);

  return {
    data: meetings,
    isFetching,
    isPending,
    error,
    hasNextPage,
    fetchNextPage,
    refreshAfter,
    refetch,
  };
}
